import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { affiliatesAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, Field, inputCls, Modal, panelCls, StatusBadge } from '../../components/ui';

function PlanModal({ plan, onClose, onSaved }) {
  const [form, setForm] = useState(plan || {
    name: '', reward_currency: 'cash', kind: 'percent', value: 20,
    trigger: 'payment', frequency: 'first', max_payments: 0, active: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, value: Number(form.value), max_payments: Number(form.max_payments || 0) };
      if (plan) await affiliatesAPI.updatePlan(plan.id, payload);
      else await affiliatesAPI.createPlan(payload);
      toast.success(t('Saved'));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={plan ? t('Edit plan') : t('New plan')} onClose={onClose} wide>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label={t('Name')}>
            <input className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
        </div>
        <Field label={t('Reward currency')}>
          <select className={inputCls} value={form.reward_currency} onChange={(e) => set({ reward_currency: e.target.value })}>
            <option value="cash">{t('cash')}</option>
            <option value="credit">{t('credit')}</option>
            <option value="points">{t('points')}</option>
          </select>
        </Field>
        <Field label={t('Kind')}>
          <select className={inputCls} value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
            <option value="percent">{t('percent of payment')}</option>
            <option value="flat">{t('flat amount')}</option>
          </select>
        </Field>
        <Field label={form.kind === 'percent' ? t('Percent value') : t('Flat value')}>
          <input type="number" step="any" className={inputCls} value={form.value}
            onChange={(e) => set({ value: e.target.value })} />
        </Field>
        <Field label={t('Trigger')}>
          <select className={inputCls} value={form.trigger} onChange={(e) => set({ trigger: e.target.value })}>
            <option value="payment">{t('payment')}</option>
            <option value="signup">{t('signup')}</option>
          </select>
        </Field>
        <Field label={t('Frequency')}>
          <select className={inputCls} value={form.frequency} onChange={(e) => set({ frequency: e.target.value })}>
            <option value="first">{t('first payment only')}</option>
            <option value="recurring">{t('recurring')}</option>
          </select>
        </Field>
        <Field label={t('Max payments (0 = unlimited)')}>
          <input type="number" min="0" className={inputCls} value={form.max_payments}
            onChange={(e) => set({ max_payments: e.target.value })} />
        </Field>
        <label className="flex items-center gap-2 self-end text-sm text-mms-muted">
          <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
          {t('Active')}
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={save} disabled={busy} className={btnPrimary}>{busy ? t('Saving…') : t('Save')}</button>
      </div>
    </Modal>
  );
}

export default function ProgramPage() {
  const [program, setProgram] = useState(null);
  const [plans, setPlans] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pages, setPages] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', plan_id: '' });
  const [editing, setEditing] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    affiliatesAPI.program().then(({ data }) => setProgram(data.program)).catch(() => toast.error(t('Could not load the program')));
    affiliatesAPI.plans().then(({ data }) => setPlans(data.plans)).catch(() => {});
    affiliatesAPI.groups().then(({ data }) => setGroups(data.items)).catch(() => {});
    affiliatesAPI.pages().then(({ data }) => setPages(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const saveProgram = async () => {
    setBusy(true);
    try {
      const toList = (v) => (Array.isArray(v) ? v : String(v || '').split(',').map((s) => s.trim()).filter(Boolean));
      await affiliatesAPI.putProgram({
        ...program,
        cookie_days: Number(program.cookie_days),
        holding_days: Number(program.holding_days),
        min_payout: Number(program.min_payout),
        points_expiry_months: Number(program.points_expiry_months || 0),
        allowed_link_hosts: toList(program.allowed_link_hosts),
        blocked_email_domains: toList(program.blocked_email_domains),
        global_caps: {
          points_per_day: Number(program.global_caps?.points_per_day || 0),
          points_per_month: Number(program.global_caps?.points_per_month || 0),
        },
        dual_sided: {
          enabled: Boolean(program.dual_sided?.enabled),
          currency: program.dual_sided?.currency || 'credit',
          amount: Number(program.dual_sided?.amount || 0),
        },
      });
      toast.success(t('Saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const removePlan = async (p) => {
    if (!window.confirm(t('Delete this plan?'))) return;
    try {
      await affiliatesAPI.deletePlan(p.id);
      toast.success(t('Deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  if (program === null) return <p className="text-sm text-mms-muted">{t('Loading…')}</p>;

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold">{t('Program')}</h1>

      <div className={`${panelCls} p-5`}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={t('Cookie window (days)')}>
            <input type="number" min="1" className={inputCls} value={program.cookie_days}
              onChange={(e) => setProgram({ ...program, cookie_days: e.target.value })} />
          </Field>
          <Field label={t('Attribution')}>
            <select className={inputCls} value={program.attribution}
              onChange={(e) => setProgram({ ...program, attribution: e.target.value })}>
              <option value="last">{t('last touch')}</option>
              <option value="first">{t('first touch')}</option>
            </select>
          </Field>
          <Field label={t('Holding period (days)')}>
            <input type="number" min="0" className={inputCls} value={program.holding_days}
              onChange={(e) => setProgram({ ...program, holding_days: e.target.value })} />
          </Field>
          <Field label={t('Minimum payout')}>
            <input type="number" min="0" step="any" className={inputCls} value={program.min_payout}
              onChange={(e) => setProgram({ ...program, min_payout: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t('Allowed link hosts (comma-separated)')} hint={t('Short links may only target these domains')}>
              <input className={inputCls}
                value={Array.isArray(program.allowed_link_hosts) ? program.allowed_link_hosts.join(', ') : program.allowed_link_hosts}
                onChange={(e) => setProgram({ ...program, allowed_link_hosts: e.target.value })} />
            </Field>
          </div>
          <Field label={t('Global points cap / day (0 = off)')}>
            <input type="number" min="0" className={inputCls} value={program.global_caps?.points_per_day ?? 0}
              onChange={(e) => setProgram({ ...program, global_caps: { ...program.global_caps, points_per_day: e.target.value } })} />
          </Field>
          <Field label={t('Global points cap / month (0 = off)')}>
            <input type="number" min="0" className={inputCls} value={program.global_caps?.points_per_month ?? 0}
              onChange={(e) => setProgram({ ...program, global_caps: { ...program.global_caps, points_per_month: e.target.value } })} />
          </Field>
          <Field label={t('Points expiry (months of inactivity, 0 = never)')}>
            <div className="flex gap-2">
              <input type="number" min="0" className={inputCls} value={program.points_expiry_months ?? 0}
                onChange={(e) => setProgram({ ...program, points_expiry_months: e.target.value })} />
              <button className={btnGhost} onClick={() =>
                affiliatesAPI.runExpiry()
                  .then(({ data }) => toast.success(`${t('Expiry run')}: ${data.expired} ${t('members')}${data.note ? ` — ${data.note}` : ''}`))
                  .catch((err) => toast.error(err.response?.data?.detail || t('Action failed')))
              }>{t('Run now')}</button>
            </div>
          </Field>
          <div className="sm:col-span-2">
            <Field label={t('Extra blocked email domains (comma-separated)')} hint={t('Well-known disposable providers are always blocked')}>
              <input className={inputCls}
                value={Array.isArray(program.blocked_email_domains) ? program.blocked_email_domains.join(', ') : (program.blocked_email_domains || '')}
                onChange={(e) => setProgram({ ...program, blocked_email_domains: e.target.value })} />
            </Field>
          </div>
          <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
            <label className="flex items-center gap-2 self-center text-sm text-mms-muted">
              <input type="checkbox" checked={Boolean(program.dual_sided?.enabled)}
                onChange={(e) => setProgram({ ...program, dual_sided: { ...program.dual_sided, enabled: e.target.checked } })} />
              {t('Dual-sided reward: the referred friend gets')}
            </label>
            <input type="number" step="any" min="0" className={`${inputCls} !w-24`}
              value={program.dual_sided?.amount ?? 5}
              onChange={(e) => setProgram({ ...program, dual_sided: { ...program.dual_sided, amount: e.target.value } })} />
            <select className={`${inputCls} !w-28`} value={program.dual_sided?.currency || 'credit'}
              onChange={(e) => setProgram({ ...program, dual_sided: { ...program.dual_sided, currency: e.target.value } })}>
              <option value="credit">{t('credit')}</option>
              <option value="points">{t('points')}</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={saveProgram} disabled={busy} className={btnPrimary}>
            {busy ? t('Saving…') : t('Save')}
          </button>
        </div>
      </div>

      <div className="mt-6 mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-mms-muted">{t('Commission plans')}</h2>
        <button onClick={() => setEditing(null)} className={btnGhost}><Plus size={14} /> {t('New plan')}</button>
      </div>
      <div className={`${panelCls} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
              <th className="px-4 py-2.5 font-medium">{t('Name')}</th>
              <th className="px-4 py-2.5 font-medium">{t('Reward')}</th>
              <th className="px-4 py-2.5 font-medium">{t('Trigger')}</th>
              <th className="px-4 py-2.5 font-medium">{t('Frequency')}</th>
              <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-b border-mms-border/60 last:border-0">
                <td className="px-4 py-2.5">
                  {p.name}
                  {p.is_base ? <span className="ml-1.5 text-xs text-mms-faint">({t('base')})</span> : null}
                </td>
                <td className="px-4 py-2.5 mms-num">
                  {p.kind === 'percent' ? `${p.value}%` : p.value} {t(p.reward_currency)}
                </td>
                <td className="px-4 py-2.5 text-xs text-mms-muted">{t(p.trigger)}</td>
                <td className="px-4 py-2.5 text-xs text-mms-muted">
                  {t(p.frequency)}{p.max_payments ? ` · ${t('max')} ${p.max_payments}` : ''}
                </td>
                <td className="px-4 py-2.5"><StatusBadge status={p.active ? 'active' : 'inactive'} /></td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className={`${btnGhost} mr-2`}>{t('Edit')}</button>
                  {!p.is_base && <button onClick={() => removePlan(p)} className={btnDanger}>{t('Delete')}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 mb-2">
        <h2 className="text-sm font-semibold text-mms-muted">{t('Affiliate groups')}</h2>
      </div>
      <div className={`${panelCls} p-5`}>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Field label={t('Name')}>
            <input className={`${inputCls} !w-48`} value={groupForm.name}
              onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} />
          </Field>
          <Field label={t('Group plan (optional)')}>
            <select className={`${inputCls} !w-52`} value={groupForm.plan_id}
              onChange={(e) => setGroupForm({ ...groupForm, plan_id: e.target.value })}>
              <option value="">{t('Base plan')}</option>
              {plans.filter((p) => !p.is_base).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <button className={btnPrimary} disabled={!groupForm.name.trim()} onClick={() =>
            affiliatesAPI.createGroup({ name: groupForm.name, plan_id: groupForm.plan_id || null })
              .then(() => { setGroupForm({ name: '', plan_id: '' }); toast.success(t('Saved')); load(); })
              .catch((err) => toast.error(err.response?.data?.detail || t('Save failed')))
          }>{t('Add')}</button>
        </div>
        {groups.map((g) => (
          <div key={g.id} className="flex items-center gap-3 text-sm">
            <span className="font-medium">{g.name}</span>
            <span className="text-xs text-mms-muted">
              {g.plan_name || t('Base plan')} · {g.members} {t('members')}
            </span>
            <button className={btnDanger} onClick={() =>
              affiliatesAPI.deleteGroup(g.id).then(load)
                .catch((err) => toast.error(err.response?.data?.detail || t('Delete failed')))
            }>{t('Delete')}</button>
          </div>
        ))}
      </div>

      {pages && (
        <>
          <div className="mt-6 mb-2">
            <h2 className="text-sm font-semibold text-mms-muted">{t('Program pages (public: /terms, /disclaimer)')}</h2>
          </div>
          <div className={`${panelCls} space-y-4 p-5`}>
            <Field label={t('Program terms')}>
              <textarea rows={5} className={inputCls} value={pages.terms}
                onChange={(e) => setPages({ ...pages, terms: e.target.value })} />
            </Field>
            <Field label={t('Disclaimer')}>
              <textarea rows={3} className={inputCls} value={pages.disclaimer}
                onChange={(e) => setPages({ ...pages, disclaimer: e.target.value })} />
            </Field>
            <div className="flex justify-end">
              <button className={btnPrimary} onClick={() =>
                affiliatesAPI.putPages(pages).then(() => toast.success(t('Saved')))
                  .catch((err) => toast.error(err.response?.data?.detail || t('Save failed')))
              }>{t('Save')}</button>
            </div>
          </div>
        </>
      )}

      {editing !== undefined && (
        <PlanModal plan={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}
